/**
 * PaintSystemsAdminPage — Standalone catalogue editor for the
 * `paint_systems` collection.
 *
 * Prior to this page, per-coat breakdown editing lived buried inside
 * an appraisal's Restoration → Stage-2 Paint panel. Now the office
 * can maintain every roof-subtype's paint system independently:
 *
 *   - See all systems at a glance (Populated / Placeholder / Archived).
 *   - Open the rich per-coat editor via the "Edit breakdowns" button.
 *   - Create new systems and delete unused ones.
 *
 * Reuses the exported `PaintBreakdownModal` from
 * `RestorationConcreteRepairs.js` so both places stay in lockstep.
 *
 * Backed by `/api/paint-systems` (GET / POST / PUT / DELETE).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Plus, Calculator, Trash2, Paintbrush, AlertCircle, Archive, Copy, ListChecks } from 'lucide-react';
import { PaintBreakdownModal } from '@/components/RestorationConcreteRepairs';
import PaintQuoteBulletsModal from '@/components/PaintQuoteBulletsModal';

// Subtypes we allow when creating a new system. Aligned with the
// canonical roof-subtype list used by _subtype_paint_match().
const ROOF_SUBTYPES = [
  'Modern Concrete', 'Old Concrete', 'Iron', 'Long-Run',
  'Decramastic', 'Colortile', 'Clay',
  'Gutter', 'Fascia', 'Garage Door', 'Fencing',
  'Generic', 'Other',
];

// Explicit display grouping — order shown in the catalogue list.
// Anything not listed falls into the "Other" bucket at the bottom.
const SUBTYPE_GROUPS = [
  { label: 'Roof Recoats',      subtypes: ['Modern Concrete', 'Old Concrete', 'Long-Run', 'Iron', 'Clay', 'Colortile', 'Decramastic'] },
  { label: 'Gutters & Fascia',  subtypes: ['Gutter', 'Fascia'] },
  { label: 'Garage Doors',      subtypes: ['Garage Door'] },
  { label: 'Fencing',           subtypes: ['Fencing'] },
  { label: 'Other',             subtypes: ['Generic', 'Other'] },
];
// Rank map for stable sorting.
const SUBTYPE_RANK = SUBTYPE_GROUPS.reduce((acc, g, gi) => {
  g.subtypes.forEach((s, si) => { acc[s] = gi * 100 + si; });
  return acc;
}, {});
const groupOfSubtype = (s) => SUBTYPE_GROUPS.find(g => g.subtypes.includes(s)) || SUBTYPE_GROUPS[SUBTYPE_GROUPS.length - 1];

// A coat is considered "populated" only when it carries actual
// pricing data — a supplier name alone isn't enough (we set that
// on placeholder coats too so audit chips can target them).
function coatIsPopulated(coat) {
  return !!(
    coat.container_price
    || coat.material_hourly_rate
    || coat.buy_per_m2
    || coat.material_per_m2
  );
}

function systemStatus(sys) {
  if (sys.archived_at) return { key: 'archived', label: 'Archived', pill: 'bg-slate-200 text-slate-600' };
  const coats = sys.coats || [];
  if (coats.length === 0) return { key: 'empty', label: 'No coats', pill: 'bg-slate-100 text-slate-500' };
  const populated = coats.filter(coatIsPopulated).length;
  if (populated === coats.length) return { key: 'populated', label: 'Populated', pill: 'bg-emerald-100 text-emerald-800' };
  if (populated === 0) return { key: 'placeholder', label: 'Placeholder', pill: 'bg-amber-100 text-amber-800' };
  return { key: 'partial', label: `${populated}/${coats.length} populated`, pill: 'bg-blue-100 text-blue-800' };
}

function systemTotals(sys) {
  const acc = { buy: 0, material: 0, labour: 0, contractor: 0 };
  for (const c of (sys.coats || [])) {
    acc.buy       += Number(c.buy_per_m2 || 0);
    acc.material  += Number(c.material_per_m2 || 0);
    acc.labour    += Number(c.labour_per_m2 || 0);
    acc.contractor+= Number(c.contractor_per_m2 || 0);
  }
  acc.sell = acc.material + acc.labour;
  return acc;
}

export default function PaintSystemsAdminPage() {
  const { api, user } = useAuth();
  const isAdmin = (user?.role || '') === 'admin';
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('active'); // active | populated | placeholder | archived | all
  const [editing, setEditing] = useState(null);   // paint system currently open in modal
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ system_name: '', roof_subtype: 'Modern Concrete', notes: '' });
  const [bulletsFor, setBulletsFor] = useState(null);
  // ── Duplicate-system dialog state (T1) ──────────────────────────────
  const [dupSource, setDupSource] = useState(null);       // the paint_system being duplicated
  const [dupForm, setDupForm] = useState({ system_name: '', roof_subtype: 'Modern Concrete' });

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get('/paint-systems');
      const list = Array.isArray(data) ? data : (data?.paint_systems || data?.systems || []);
      // Backend returns `_id` but the modal (originally built for the
      // Restoration flow) reads `.id`. Alias here so both work.
      setSystems(list.map(s => ({ ...s, id: s.id || s._id })));
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return systems.filter(s => {
      if (filter === 'all') return true;
      if (filter === 'archived') return !!s.archived_at;
      if (filter === 'active') return !s.archived_at;
      if (filter === 'populated') return !s.archived_at && systemStatus(s).key === 'populated';
      if (filter === 'placeholder') return !s.archived_at && (systemStatus(s).key === 'placeholder' || systemStatus(s).key === 'partial');
      return true;
    }).sort((a, b) => {
      // Group order first, then alphabetical within group.
      const ra = SUBTYPE_RANK[a.roof_subtype] ?? 9999;
      const rb = SUBTYPE_RANK[b.roof_subtype] ?? 9999;
      if (ra !== rb) return ra - rb;
      return (a.system_name || '').localeCompare(b.system_name || '');
    });
  }, [systems, filter]);

  const openEdit = (sys) => setEditing(sys);
  const closeEdit = () => setEditing(null);
  const onSaved = async () => {
    toast.success('Breakdown saved');
    await load();
    setEditing(null);
  };

  const createSystem = async () => {
    if (!newForm.system_name.trim()) { toast.error('System name is required'); return; }
    try {
      const payload = {
        system_name:  newForm.system_name.trim(),
        roof_subtype: newForm.roof_subtype,
        notes:        newForm.notes.trim(),
        coats:        [],
      };
      const { data } = await api.post('/paint-systems', payload);
      toast.success(`Created "${data.system_name || newForm.system_name}"`);
      setCreating(false);
      setNewForm({ system_name: '', roof_subtype: 'Modern Concrete', notes: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Create failed');
    }
  };

  const deleteSystem = async (sys) => {
    if (!window.confirm(
      `Archive "${sys.system_name}"?\n\n`
      + `It will be hidden from the Active list but kept in history. `
      + `You can restore it later from the Archived tab.`
    )) return;
    try {
      await api.delete(`/paint-systems/${sys.id || sys._id}`);
      toast.success(`Archived "${sys.system_name}"`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Archive failed');
    }
  };

  // Un-archive a previously-archived paint system.
  const unarchiveSystem = async (sys) => {
    try {
      await api.put(`/paint-systems/${sys.id || sys._id}`, {
        system_name:  sys.system_name,
        roof_subtype: sys.roof_subtype,
        notes:        sys.notes || '',
        coats:        sys.coats || [],
        archived_at:  '',
      });
      toast.success(`Restored "${sys.system_name}"`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Restore failed');
    }
  };

  // ── Duplicate flow (T1) ───────────────────────────────────────────
  const openDuplicate = (sys) => {
    setDupSource(sys);
    setDupForm({
      system_name:  `${sys.system_name} (copy)`,
      roof_subtype: sys.roof_subtype || 'Modern Concrete',
    });
  };
  const closeDuplicate = () => {
    setDupSource(null);
    setDupForm({ system_name: '', roof_subtype: 'Modern Concrete' });
  };
  const submitDuplicate = async () => {
    if (!dupSource) return;
    if (!dupForm.system_name.trim()) { toast.error('System name is required'); return; }
    try {
      // Deep-clone the coats — preserve every field but drop any
      // per-document IDs so the backend can generate fresh ones.
      const clonedCoats = (dupSource.coats || []).map(c => {
        const { _id: _mongoId, id: _pubId, ...rest } = c;
        void _mongoId; void _pubId;
        return { ...rest };
      });
      const payload = {
        system_name:  dupForm.system_name.trim(),
        roof_subtype: dupForm.roof_subtype,
        notes:        dupSource.notes || '',
        coats:        clonedCoats,
      };
      const { data } = await api.post('/paint-systems', payload);
      toast.success(`Duplicated → "${data.system_name || dupForm.system_name}"`);
      closeDuplicate();
      await load();
      if (data && (data.id || data._id)) setEditing({ ...data, id: data.id || data._id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Duplicate failed');
    }
  };


  const counts = useMemo(() => {
    const active = systems.filter(s => !s.archived_at);
    return {
      total:       systems.length,
      active:      active.length,
      populated:   active.filter(s => systemStatus(s).key === 'populated').length,
      placeholder: active.filter(s => { const k = systemStatus(s).key; return k === 'placeholder' || k === 'partial'; }).length,
      archived:    systems.filter(s => !!s.archived_at).length,
    };
  }, [systems]);

  if (!isAdmin) {
    return (
      <div className="max-w-5xl mx-auto p-8">
        <div className="bg-rose-50 border border-rose-300 rounded-md p-4 text-sm text-rose-800 flex items-center gap-3">
          <AlertCircle size={18} /> This page requires admin role.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="paint-systems-admin-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <Paintbrush size={22} className="text-emerald-700" />
        <h1 className="text-2xl font-bold text-slate-900">Paint Systems Catalogue</h1>
      </div>
      <p className="text-sm text-slate-600 mb-6">
        Per-coat breakdown editor for every roof subtype&apos;s paint system. Prices flow into the appraisal Stage-2 Paint panel.
      </p>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-slate-200">
        {[
          { key: 'active',      label: 'Active',      count: counts.active,      testid: 'filter-active' },
          { key: 'populated',   label: 'Populated',   count: counts.populated,   testid: 'filter-populated' },
          { key: 'placeholder', label: 'Needs pricing', count: counts.placeholder, testid: 'filter-placeholder' },
          { key: 'archived',    label: 'Archived',    count: counts.archived,    testid: 'filter-archived' },
          { key: 'all',         label: 'All',         count: counts.total,       testid: 'filter-all' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            data-testid={`paint-systems-${t.testid}`}
            className={`px-3 py-2 text-xs font-semibold rounded-t transition-colors border-b-2 -mb-px ${
              filter === t.key
                ? 'text-emerald-800 border-emerald-600 bg-emerald-50/40'
                : 'text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            {t.label} <span className="ml-1 opacity-70 font-normal">({t.count})</span>
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 pb-1">
          <button
            type="button"
            onClick={() => setCreating(v => !v)}
            data-testid="paint-systems-new-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus size={12} /> New system
          </button>
        </div>
      </div>

      {/* New-system inline form */}
      {creating && (
        <div className="mb-4 p-4 border border-emerald-300 bg-emerald-50/40 rounded-md" data-testid="paint-systems-new-form">
          <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 mb-2">Create new paint system</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">System name</span>
              <input
                value={newForm.system_name}
                onChange={e => setNewForm(f => ({ ...f, system_name: e.target.value }))}
                data-testid="paint-systems-new-name"
                placeholder="e.g. Iron Rust Guard 4-stage"
                className="text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Roof subtype</span>
              <select
                value={newForm.roof_subtype}
                onChange={e => setNewForm(f => ({ ...f, roof_subtype: e.target.value }))}
                data-testid="paint-systems-new-subtype"
                className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400"
              >
                {ROOF_SUBTYPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Notes (optional)</span>
              <input
                value={newForm.notes}
                onChange={e => setNewForm(f => ({ ...f, notes: e.target.value }))}
                data-testid="paint-systems-new-notes"
                placeholder="Short description shown in the Stage 2 header"
                className="text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => { setCreating(false); setNewForm({ system_name: '', roof_subtype: 'Modern Concrete', notes: '' }); }}
              className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900"
              data-testid="paint-systems-new-cancel"
            >Cancel</button>
            <button
              type="button"
              onClick={createSystem}
              data-testid="paint-systems-new-submit"
              className="px-4 py-1.5 text-xs font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700"
            >Create</button>
          </div>
        </div>
      )}

      <div data-testid="paint-systems-source-banner" className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
        <strong>Client prices come from Component Rate Tables → Re-Coat</strong> (Materials $ + Labour $).
        {' '}Each system here is the coat recipe; its Material/Labour $ per coat are scaled to the linked row and are read-only in the editor.
        {' '}<a href="/component-rates?component=recoat" className="underline font-semibold">Edit rates →</a>
      </div>

      {/* Body */}
      {loading ? (
        <div className="p-8 text-center text-slate-500 text-sm">
          <Loader2 size={16} className="inline animate-spin mr-2" /> Loading paint systems…
        </div>
      ) : err ? (
        <div className="bg-rose-50 border border-rose-300 rounded-md p-3 text-sm text-rose-700 flex items-center gap-2">
          <AlertCircle size={14} /> {err}
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center text-slate-400 text-sm border border-dashed border-slate-300 rounded-md">
          No systems match the current filter.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-md bg-white">
          <table className="w-full text-xs" data-testid="paint-systems-table">
            <thead className="text-slate-500 bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-2.5 px-3 font-semibold w-40">Roof subtype</th>
                <th className="text-left py-2.5 px-3 font-semibold">System name</th>
                <th className="text-center py-2.5 px-2 font-semibold w-16">Stages</th>
                <th className="text-center py-2.5 px-2 font-semibold w-32">Status</th>
                <th className="text-right py-2.5 px-2 font-semibold w-20">Buy $/m²</th>
                <th className="text-right py-2.5 px-2 font-semibold w-24">Coat sum $/m²</th>
                <th className="text-right py-2.5 px-2 font-semibold w-44 bg-emerald-50" title="Materials $ + Labour $ from Component Rate Tables → Re-Coat">Price (Rate Table)</th>
                <th className="text-right py-2.5 px-3 font-semibold w-52">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const rows = [];
                let currentGroup = null;
                filtered.forEach((sys) => {
                  const grp = groupOfSubtype(sys.roof_subtype).label;
                  if (grp !== currentGroup) {
                    currentGroup = grp;
                    rows.push(
                      <tr key={`group-${grp}`} className="bg-slate-100 border-t border-slate-200">
                        <td colSpan={8} className="py-1.5 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-600" data-testid={`paint-systems-group-${grp.replace(/\s+/g, '-').toLowerCase()}`}>
                          {grp}
                        </td>
                      </tr>
                    );
                  }
                  const status = systemStatus(sys);
                  const totals = systemTotals(sys);
                  const isArch = !!sys.archived_at;
                  rows.push(
                  <tr key={sys.id || sys._id} data-testid={`paint-systems-row-${sys.id || sys._id}`} className={`border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${isArch ? 'opacity-60' : ''}`}>
                    <td className="py-2.5 px-3">
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                        {sys.roof_subtype || '—'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-semibold text-slate-900">{sys.system_name}</div>
                      {sys.notes && <div className="text-[10px] text-slate-500 mt-0.5 truncate max-w-md" title={sys.notes}>{sys.notes}</div>}
                    </td>
                    <td className="py-2.5 px-2 text-center text-slate-700 font-mono-num">
                      {(sys.coats || []).length}
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${status.pill}`}>
                        {isArch && <Archive size={9} />}
                        {status.label}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-right text-slate-700 font-mono-num">
                      ${totals.buy.toFixed(2)}
                    </td>
                    <td className="py-2.5 px-2 text-right font-mono-num text-slate-500">
                      ${totals.sell.toFixed(2)}
                    </td>
                    <td className="py-2.5 px-2 text-right font-mono-num bg-emerald-50/40" data-testid={`paint-systems-price-${sys.id || sys._id}`}>
                      {sys.rate_row ? (
                        <a href="/component-rates?component=recoat" className="text-emerald-800 font-bold hover:underline" title={`Priced from ${sys.rate_row.name}`}>
                          ${sys.rate_row.rtr_rate.toFixed(2)}/{sys.rate_row.unit}
                        </a>
                      ) : (
                        <span className="text-rose-600 text-[10px] font-semibold">No rate row linked</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <button
                        type="button"
                        onClick={() => openEdit(sys)}
                        data-testid={`paint-systems-edit-${sys.id || sys._id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wide bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-700 transition-all"
                      >
                        <Calculator size={10} /> Edit breakdowns
                      </button>
                      <button
                        type="button"
                        onClick={() => setBulletsFor(sys)}
                        data-testid={`paint-systems-bullets-${sys.id || sys._id}`}
                        title="Quote letter bullets for this system"
                        className="inline-flex items-center gap-1 ml-1 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wide bg-white border border-slate-300 text-slate-700 hover:bg-slate-700 hover:text-white transition-all"
                      >
                        <ListChecks size={10} /> Bullets{(sys.quote_bullets || []).length ? ` (${sys.quote_bullets.length})` : ''}
                      </button>
                      <button
                        type="button"
                        onClick={() => openDuplicate(sys)}
                        data-testid={`paint-systems-duplicate-${sys.id || sys._id}`}
                        title="Duplicate this paint system"
                        className="inline-flex items-center gap-1 ml-1 px-2 py-1 rounded text-[10px] text-slate-500 hover:text-blue-700 hover:bg-blue-50 transition-all"
                      >
                        <Copy size={10} />
                      </button>
                      {isArch ? (
                        <button
                          type="button"
                          onClick={() => unarchiveSystem(sys)}
                          data-testid={`paint-systems-unarchive-${sys.id || sys._id}`}
                          title="Restore this archived paint system"
                          className="inline-flex items-center gap-1 ml-1 px-2 py-1 rounded text-[10px] text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 transition-all"
                        >
                          <Archive size={10} className="rotate-180" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => deleteSystem(sys)}
                          data-testid={`paint-systems-delete-${sys.id || sys._id}`}
                          title="Archive this paint system (soft delete — restore from Archived tab)"
                          className="inline-flex items-center gap-1 ml-1 px-2 py-1 rounded text-[10px] text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all"
                        >
                          <Trash2 size={10} />
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                });
                return rows;
              })()}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal — reused from RestorationConcreteRepairs */}
      {editing && (
        <PaintBreakdownModal
          api={api}
          paintSystem={editing}
          onClose={closeEdit}
          onSaved={onSaved}
        />
      )}

      {/* Duplicate-system dialog (T1) */}
      {dupSource && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
          onClick={closeDuplicate}
          data-testid="paint-systems-duplicate-dialog"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-lg my-16"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-slate-200 bg-blue-50 flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">Duplicate paint system</div>
                <h3 className="text-lg font-bold text-slate-900 mt-0.5">{dupSource.system_name}</h3>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {(dupSource.coats || []).length} coat{(dupSource.coats || []).length !== 1 ? 's' : ''} will be copied verbatim.
                </div>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">New system name</span>
                <input
                  value={dupForm.system_name}
                  onChange={(e) => setDupForm(f => ({ ...f, system_name: e.target.value }))}
                  data-testid="paint-systems-duplicate-name"
                  className="text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Roof subtype for the copy</span>
                <select
                  value={dupForm.roof_subtype}
                  onChange={(e) => setDupForm(f => ({ ...f, roof_subtype: e.target.value }))}
                  data-testid="paint-systems-duplicate-subtype"
                  className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  {ROOF_SUBTYPES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeDuplicate}
                data-testid="paint-systems-duplicate-cancel"
                className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 rounded"
              >Cancel</button>
              <button
                type="button"
                onClick={submitDuplicate}
                data-testid="paint-systems-duplicate-submit"
                className="px-4 py-1.5 text-xs font-bold bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1.5"
              >
                <Copy size={11} /> Duplicate &amp; open
              </button>
            </div>
          </div>
        </div>
      )}
      {bulletsFor && <PaintQuoteBulletsModal api={api} system={bulletsFor} onClose={() => setBulletsFor(null)} onSaved={load} />}
    </div>
  );
}
